/**
 * Knowledge resources — typed wrappers over `/v1/twin/insights[/{id}]` and
 * `/v1/twin/interventions[/{id}]` plus server→UI mappers. Back the durable
 * persistence for CIE Pillar 2 (B3.x), replacing the SPA's localStorage
 * insights store in live mode.
 *
 * Both `data` fields are opaque JSON the UI lane owns — they *are* the full
 * `TenantInsight` / `InterventionLogEntry` record. The mappers are
 * near-identity: hydrate `data`, then let the envelope's `id` win. POST is an
 * upsert keyed on the client-owned text id (`ins-…` / `int-…`).
 */

import { api } from "@/lib/api-client";
import type { InterventionLogEntry, TenantInsight } from "@/lib/insights/types";
import type { Paginated } from "./types";

/** A tenant_insights row (migration 0036). `data` carries the TenantInsight. */
export interface InsightRow {
  id: string;
  org_id: string;
  kind: string;
  data: unknown;
  created_at: string;
  updated_at: string;
}

/** An intervention_log row (migration 0036). `data` carries the entry. */
export interface InterventionRow {
  id: string;
  org_id: string;
  data: unknown;
  created_at: string;
  updated_at: string;
}

export const insights = {
  list: () => api.get<Paginated<InsightRow>>("/v1/twin/insights"),
  /** Upsert on the client-owned id. */
  upsert: (body: { id: string; kind: string; data: unknown }) =>
    api.post<InsightRow>("/v1/twin/insights", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/insights/${id}`),
};

export const interventions = {
  list: () => api.get<Paginated<InterventionRow>>("/v1/twin/interventions"),
  /** Upsert on the client-owned id. */
  upsert: (body: { id: string; data: unknown }) =>
    api.post<InterventionRow>("/v1/twin/interventions", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/interventions/${id}`),
};

/** Bridge an insight row to the UI TenantInsight (envelope id wins). */
export function toTenantInsight(r: InsightRow): TenantInsight {
  const data = (r.data && typeof r.data === "object" ? r.data : {}) as Partial<TenantInsight>;
  return { ...(data as TenantInsight), id: r.id };
}

/** Bridge an intervention row to the UI InterventionLogEntry. */
export function toInterventionEntry(r: InterventionRow): InterventionLogEntry {
  const data = (r.data && typeof r.data === "object"
    ? r.data
    : {}) as Partial<InterventionLogEntry>;
  return { ...(data as InterventionLogEntry), id: r.id };
}
